import { useGetDocuments } from "@hooks";
import type { Document } from "@models";
import { RegulatoryTypeEnum } from "@types";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCustomTemplate } from "src/hooks/useCustomTemplate";
import { findInstancesOfTemplateKey } from "src/utils/documents";
import {
  clearTransientDocumentSearchParams,
  getDocumentNavigationUrl,
} from "src/utils/navigation";

type UseTiptapRecordOptionsArgs = {
  deviceId: string;
  document?: Document | null;
  enabled?: boolean;
  orgId?: string;
};

export type TiptapRecordDropdownOptions = {
  docs: Document[];
  isCreatingNewInstance: boolean;
  isDocEnabled: boolean;
  onCreateDocInstance: () => void;
  onSelectDocInstance: (document: Document) => void;
  selectedDoc: Document;
};

export function useTiptapRecordOptions({
  deviceId,
  document,
  enabled = true,
  orgId,
}: UseTiptapRecordOptionsArgs): TiptapRecordDropdownOptions | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreatingNewRecord, setIsCreatingNewRecord] = useState(false);
  const templateKey = document?.templateKey ?? "";
  const recordDeviceId = document?.deviceId || deviceId;
  const hasRecordContext = Boolean(
    enabled &&
      orgId &&
      document &&
      recordDeviceId &&
      templateKey &&
      document.regulatoryType === RegulatoryTypeEnum.RCD
  );
  const { data: documents } = useGetDocuments(
    orgId ?? "",
    recordDeviceId,
    {
      enabled: hasRecordContext,
    }
  );
  const { createNewInstance } = useCustomTemplate(orgId ?? "", recordDeviceId);

  const recordDocuments = useMemo(() => {
    if (!hasRecordContext || !document || !documents) {
      return [];
    }

    const instances = findInstancesOfTemplateKey(document, documents);

    return instances.some((instance) => instance.id === document.id)
      ? instances
      : [document, ...instances];
  }, [document, documents, hasRecordContext]);

  const handleSelectRecord = useCallback(
    (nextDocument: Document) => {
      const nextDeviceId = nextDocument.deviceId ?? recordDeviceId;

      if (!orgId || !templateKey || !nextDeviceId) {
        return;
      }

      const nextSearchParams = clearTransientDocumentSearchParams(
        location.search
      );
      nextSearchParams.delete("tiptapVersion");
      nextSearchParams.delete("tiptapSuggestionId");

      const nextVersionId = nextDocument.versions[0]?.id;
      if (nextVersionId) {
        nextSearchParams.set("v", nextVersionId);
      }

      const nextRoute = getDocumentNavigationUrl(
        orgId,
        nextDeviceId,
        nextDocument.id
      );
      const nextSearch = nextSearchParams.toString();

      navigate(nextSearch ? `${nextRoute}?${nextSearch}` : nextRoute);
    },
    [location.search, navigate, orgId, recordDeviceId, templateKey]
  );

  const handleCreateNewRecord = useCallback(async () => {
    if (!document || !orgId || !recordDeviceId || !templateKey) {
      return;
    }

    setIsCreatingNewRecord(true);
    try {
      await createNewInstance(templateKey, document.title);
    } finally {
      setIsCreatingNewRecord(false);
    }
  }, [createNewInstance, document, orgId, recordDeviceId, templateKey]);

  if (!hasRecordContext || !document || recordDocuments.length === 0) {
    return null;
  }

  return {
    docs: recordDocuments,
    isCreatingNewInstance: isCreatingNewRecord,
    isDocEnabled: true,
    onCreateDocInstance: handleCreateNewRecord,
    onSelectDocInstance: handleSelectRecord,
    selectedDoc: document,
  };
}
